import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useUserStore } from '../stores/StoreProvider';
import { Users, UserPlus, MessageCircle, Trophy, User, UserCheck, UserX } from 'lucide-react';
import toast from 'react-hot-toast';

const Friends = observer(() => {
  const user = useUserStore();
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('following');
  const [followingIds, setFollowingIds] = useState(new Set());

  // Fetch following and followers data
  useEffect(() => {
    if (user.isAuthenticated) {
      fetchFollowing();
      fetchFollowers();
    }
  }, [user.isAuthenticated]);

  const fetchFollowing = async () => {
    try {
      const response = await user.rootStore.authenticatedApiCall('api/user-follows/following');
      setFollowing(response);
      // Create a Set of following IDs for quick lookup
      setFollowingIds(new Set(response.map(user => user.id)));
    } catch (error) {
      console.error('Error fetching following:', error);
      setFollowing([]);
      setFollowingIds(new Set());
    }
  };

  const fetchFollowers = async () => {
    try {
      const response = await user.rootStore.authenticatedApiCall('api/user-follows/followers');
      setFollowers(response);
    } catch (error) {
      console.error('Error fetching followers:', error);
      setFollowers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      await user.rootStore.authenticatedApiCall(`api/user-follows/${userId}/follow`, {}, 'DELETE');
      toast.success('User unfollowed');
      fetchFollowing(); // Refresh the list
    } catch (error) {
      console.error('Error unfollowing user:', error);
      toast.error('Failed to unfollow user');
    }
  };

  const handleFollow = async (userId) => {
    try {
      await user.rootStore.authenticatedApiCall(`api/user-follows/${userId}/follow`, {}, 'POST');
      toast.success('User followed');
      fetchFollowing(); // Refresh the list to update followingIds
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Failed to follow user');
    }
  };

  const renderUserCard = (userData, isFollowing = false) => {
    // Check if we're following this user by looking up their ID in the followingIds Set
    const isCurrentlyFollowing = followingIds.has(userData.id);
    
    return (
    <div key={userData.id} className="p-6 hover:bg-gray-50 transition-colors border-b border-gray-200 last:border-b-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            {userData.avatar ? (
              <img src={userData.avatar} alt="Avatar" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <span className="text-white font-semibold">
                {userData.first_name?.charAt(0) || userData.last_name?.charAt(0) || 'U'}
              </span>
            )}
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {userData.first_name && userData.last_name 
                ? `${userData.first_name} ${userData.last_name}`
                : 'Unknown User'
              }
            </h3>
            <p className="text-sm text-gray-500">
              {userData.age} {userData.level}
              {userData.organization?.name && ` • ${userData.organization.name}`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isFollowing || isCurrentlyFollowing ? (
            <button 
              onClick={() => handleUnfollow(userData.id)}
              className="px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center space-x-2"
            >
              <UserX size={16} />
              <span>Unfollow</span>
            </button>
          ) : (
            <button 
              onClick={() => handleFollow(userData.id)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <UserPlus size={16} />
              <span>Follow</span>
            </button>
          )}
        </div>
      </div>
    </div>
    );
  };

  const renderEmptyState = (type) => (
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        {type === 'following' ? <UserCheck className="text-gray-400" size={24} /> : <Users className="text-gray-400" size={24} />}
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {type === 'following' ? 'Not Following Anyone' : 'No Followers Yet'}
      </h3>
      <p className="text-gray-500 mb-4">
        {type === 'following' 
          ? 'Start following other players to see their activity in your feed'
          : 'When other players follow you, they\'ll appear here'
        }
      </p>
      {type === 'following' && (
        <button className="btn-primary">
          <UserPlus className="mr-2" size={16} />
          Discover Players
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Friends</h1>
          <p className="text-gray-600">Connect with other hockey players</p>
        </div>
        <div className="card">
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Friends</h1>
        <p className="text-gray-600">Connect with other hockey players</p>
      </div>

      {/* Tab Navigation */}
      <div className="card">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('following')}
              className={`flex-1 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'following'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Following ({following.length})
            </button>
            <button
              onClick={() => setActiveTab('followers')}
              className={`flex-1 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'followers'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Followers ({followers.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === 'following' ? (
            following.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {following.map(user => renderUserCard(user, true))}
              </div>
            ) : (
              renderEmptyState('following')
            )
          ) : (
            followers.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {followers.map(user => renderUserCard(user, false))}
              </div>
            ) : (
              renderEmptyState('followers')
            )
          )}
        </div>
      </div>
    </div>
  );
});

export default Friends; 